import React, { useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Truck, MapPin, DollarSign, Lock, FileText, Zap, Weight, Box, CheckCircle, 
  Activity, ChevronDown, ChevronUp, MessageCircle, Phone, Package, User, Tag,
  Edit3, Trash2, Eye, Calendar, Search, Clock
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import { limparTitulo } from '@/lib/utils';

// ✅ FUNÇÃO LOCAL DE FORMATAÇÃO DE VALOR
const formatarValor = (valor) => {
  const num = Number(valor);
  if (isNaN(num) || num === 0) return 'R$ 0,00';
  
  return new Intl.NumberFormat('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

const WHATSAPP_ATENDENTE = "5565981181997";
const TAXA_AGENCIAMENTO_UNICA = 50.00;

const TIPOS_VALOR = {
  'fixo': { label: 'Valor Fixo', icon: DollarSign, bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50' },
  'tonelada': { label: 'R$/Ton', icon: Weight, bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50' },
  'metro_cubico': { label: 'R$/m³', icon: Box, bg: 'bg-violet-500', text: 'text-violet-600', light: 'bg-violet-50' },
  'a_combinar': { label: 'A Combinar', icon: DollarSign, bg: 'bg-slate-500', text: 'text-slate-600', light: 'bg-slate-50' }
};

const STATUS_CONFIG = {
  disponivel: { color: 'from-emerald-500 to-emerald-600', label: 'Disponível', icon: CheckCircle },
  negociando: { color: 'from-amber-500 to-amber-600', label: 'Negociando', icon: Activity },
  aceita: { color: 'from-blue-500 to-indigo-600', label: 'Aceita', icon: CheckCircle },
  em_transito: { color: 'from-violet-500 to-purple-600', label: 'Em Trânsito', icon: Truck },
  entregue: { color: 'from-slate-500 to-gray-600', label: 'Entregue', icon: CheckCircle },
  cancelada: { color: 'from-red-500 to-rose-600', label: 'Cancelada', icon: Activity }
};

const LiveBadge = () => (
  <motion.div
    animate={{ opacity: [1, 0.7, 1] }}
    transition={{ duration: 2, repeat: Infinity }}
    className="flex items-center gap-1.5 bg-emerald-500/20 rounded-full px-2.5 py-1 border border-emerald-400/30"
  >
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
    </span>
    <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">Live</span>
  </motion.div>
);

// 🟢 CARGAS REAIS: Disponível piscando (verde)
const BadgeDisponivelReal = () => (
  <motion.div 
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    className="bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-emerald-500/30 border border-emerald-400"
  >
    <motion.div animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }} transition={{ duration: 2, repeat: Infinity }}>
      <CheckCircle className="w-4 h-4" />
    </motion.div>
    Disponível
  </motion.div>
);

// 🟡 CARGAS FICTÍCIAS: Disponível para Análise estático (amarelo)
const BadgeDisponivelFicticia = () => (
  <motion.div 
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    className="bg-amber-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-amber-500/30 border border-amber-400"
  >
    <CheckCircle className="w-4 h-4" />
    Disponível para Análise
  </motion.div>
);

// ✅ CORRIGIDO: Mostra unidade EXATA do cadastro (sem conversão)
const formatarQuantidade = (carga) => {
  // PRIORIDADE 1: peso + peso_unidade (do cadastro)
  if (carga.peso !== undefined && carga.peso !== null && carga.peso_unidade) {
    const valor = parseFloat(carga.peso) || 0;
    if (valor === 0) return '--';
    
    // Formata número
    const numFormatado = valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: carga.peso_unidade === 'm3' ? 1 : 0
    });

    // Unidade exata do cadastro
    const unidadeLabel = carga.peso_unidade === 'ton' ? 'ton' : 
                        carga.peso_unidade === 'kg' ? 'kg' : 
                        carga.peso_unidade === 'm3' ? 'm³' : 
                        carga.peso_unidade || '';

    return `${numFormatado} ${unidadeLabel}`;
  }
  
  // PRIORIDADE 2: metros_cubicos (fallback)
  if (carga.metros_cubicos && !carga.peso) {
    const m3 = parseFloat(carga.metros_cubicos);
    if (!isNaN(m3) && m3 > 0) {
      return `${m3.toLocaleString('pt-BR', {minimumFractionDigits: 1, maximumFractionDigits: 1})} m³`;
    }
  }
  
  return '--';
};

const getIconeQuantidade = (carga) => {
  const unidade = carga.peso_unidade;
  if (unidade === 'm3' || (!unidade && carga.metros_cubicos && !carga.peso)) {
    return <Box className="w-4 h-4 text-violet-600" />;
  }
  return <Weight className="w-4 h-4 text-emerald-600" />;
};

const CargaCard = forwardRef(({ 
  carga, 
  index = 0, 
  user = null, 
  isAuthenticated = false,
  modo = 'publico',
  onEditar,
  onExcluir,
  onVerDetalhes,
  onConsultarCarga,
  isPropriaCarga = false
}, ref) => {
  const navigate = useNavigate();
  const [expandido, setExpandido] = useState(false);

  if (!carga) return null;

  const isMotorista = user?.tipo === 'motorista';
  const isEmpresa = user?.tipo === 'empresa';
  const tipoValor = carga.tipo_valor || 'fixo';
  const config = TIPOS_VALOR[tipoValor];
  const Icon = config.icon;
  const statusConfig = STATUS_CONFIG[carga.status] || STATUS_CONFIG.disponivel;
  const StatusIcon = statusConfig.icon;

  let valorTotal = 0;
  let valorLabel = '';

  if (carga.valor_total !== undefined && carga.valor_total !== null) {
    valorTotal = Number(carga.valor_total);
  }
  
  if (!valorTotal || valorTotal <= 0) {
    const valorFrete = parseFloat(carga.valor_frete) || parseFloat(carga.valor) || 0;
    const peso = parseFloat(carga.peso) || 0;
    const pesoValor = parseFloat(carga.peso_valor) || 0;
    const m3 = parseFloat(carga.metros_cubicos) || 0;
    
    if (tipoValor === 'tonelada') {
      const pesoCalc = pesoValor > 0 ? pesoValor : peso;
      valorTotal = valorFrete * pesoCalc;
      valorLabel = `${formatarValor(valorFrete)}/ton`;
    } else if (tipoValor === 'metro_cubico' && m3 > 0) {
      valorTotal = valorFrete * m3;
      valorLabel = `${formatarValor(valorFrete)}/m³`;
    } else {
      valorTotal = valorFrete;
    }
  } else {
    if (tipoValor === 'tonelada') {
      const valorFrete = parseFloat(carga.valor_frete) || 0;
      valorLabel = `${formatarValor(valorFrete)}/ton`;
    } else if (tipoValor === 'metro_cubico') {
      const valorFrete = parseFloat(carga.valor_frete) || 0;
      valorLabel = `${formatarValor(valorFrete)}/m³`;
    }
  }

  valorTotal = isNaN(valorTotal) ? 0 : valorTotal;

  const quantidadeFormatada = formatarQuantidade(carga);
  const iconeQuantidade = getIconeQuantidade(carga);

  const verificarPagamento = () => {
    if (modo !== 'publico') return false;
    try {
      const liberadas = JSON.parse(localStorage.getItem('borafrete_cargas_liberadas') || '[]');
      return liberadas.some(item => item.cargaId === carga.id);
    } catch (e) { return false; };
  };
  const jaPagou = verificarPagamento();

  const handleCardClick = () => {
    setExpandido(!expandido);
  };

  const handleConsultarClick = (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Faça login para consultar esta carga');
      navigate('/login?from=/buscar-cargas');
      return;
    }
    
    if (onConsultarCarga) {
      onConsultarCarga(carga);
    }
  };

  const handleEditar = (e) => {
    e.stopPropagation();
    if (onEditar) onEditar(carga);
  };

  const handleExcluir = (e) => {
    e.stopPropagation();
    if (onExcluir) onExcluir(carga);
  };

  const renderizarAcoes = () => {
    if (modo === 'minha') {
      return (
        <div className="flex gap-2 pt-3 border-t border-slate-700/50">
          <Button variant="outline" size="sm" className="flex-1 bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700" onClick={handleEditar}>
            <Edit3 className="w-4 h-4 mr-1" />Editar
          </Button>
          <Button variant="outline" size="sm" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={handleExcluir}>
            <Trash2 className="w-4 h-4 mr-1" />Excluir
          </Button>
        </div>
      );
    }

    if (modo === 'publico') {
      if (!isAuthenticated) {
        return (
          <div className="bg-slate-800/50 rounded-lg p-3 text-center border border-slate-700">
            <Lock className="w-5 h-5 text-slate-500 mx-auto mb-2" />
            <Button 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); navigate('/login?from=/buscar-cargas'); }}
              className="bg-slate-700 text-white hover:bg-slate-600 h-9"
            >
              Entrar para Ver
            </Button>
          </div>
        );
      }

      if (jaPagou) {
        return (
          <Button 
            className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold h-11"
            onClick={(e) => { e.stopPropagation(); navigate(`/carga/${carga.id}/contrato`); }}
          >
            <FileText className="w-4 h-4 mr-2" />
            Ver Contrato
          </Button>
        );
      }

      return (
        <Button 
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold h-12 shadow-lg relative overflow-hidden group"
          onClick={handleConsultarClick}
        >
          <Clock className="w-5 h-5 mr-2" />
          CONSULTAR CARGA
        </Button>
      );
    }

    return null;
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="h-full"
    >
      <Card 
        className={`group h-full flex flex-col border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-slate-900 overflow-hidden relative cursor-pointer ${expandido ? 'ring-2 ring-indigo-500' : ''}`}
        onClick={handleCardClick}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-slate-900 to-purple-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-500" />
        
        {/* HEADER */}
        <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-4 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '20px 20px'
            }} />
          </div>
          
          <div className="relative z-10 flex items-center justify-between mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2">
              {modo === 'publico' && <LiveBadge />}
              <Badge className={`${config.bg} text-white border-0 text-xs`}>
                <Icon className="w-3 h-3 mr-1" />
                {config.label}
              </Badge>
            </div>
          </div>

          <div className="relative z-10 mb-4">
            <CardTitle className="text-lg font-bold text-white line-clamp-1 mb-3">
              {limparTitulo(carga.titulo)}
            </CardTitle>
            {/* ✅ CORRIGIDO: Badge diferencia fictícias de reais */}
            {modo === 'publico' && carga.status === 'disponivel' && (
              <>
                {carga.carga_ficticia === true ? <BadgeDisponivelFicticia /> : <BadgeDisponivelReal />}
              </>
            )}
            {modo === 'minha' && (
              <Badge className={`bg-gradient-to-r ${statusConfig.color} text-white border-0`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </Badge>
            )}
          </div>
        </div>

        <CardContent className="flex-1 p-4 space-y-4 relative z-10">
          {/* VALOR */}
          <div className={`${config.light} rounded-xl p-4 border border-slate-700`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-slate-400 uppercase mb-1 font-medium">Valor Total Estimado</p>
                <p className="text-2xl font-bold text-white">{formatarValor(valorTotal)}</p>
                {valorLabel && <p className="text-xs text-slate-400 mt-1">{valorLabel}</p>}
              </div>
              <div className={`w-12 h-12 rounded-full ${config.bg} flex items-center justify-center shadow-lg`}>
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* EXPANDIDO - DETALHES DA CARGA */}
          <AnimatePresence>
            {expandido && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4 border-t border-slate-700/50 pt-4"
              >
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3 border border-slate-700">
                  <h4 className="font-semibold text-slate-200 flex items-center gap-2">
                    <Package className="w-4 h-4 text-indigo-400" />
                    Detalhes Completos
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">ID</p>
                      <p className="font-medium text-slate-300">#{carga?.id?.slice(-8).toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Tipo</p>
                      <p className="font-medium text-slate-300">{carga?.tipo_carga || 'Geral'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Retirada</p>
                      <p className="font-medium text-slate-300 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {carga?.data_coleta || carga?.data_retirada || 'A combinar'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Entrega</p>
                      <p className="font-medium text-slate-300 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {carga?.data_entrega || 'A combinar'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                    <p className="text-xs text-purple-400 font-medium mb-1 flex items-center gap-1">
                      <Weight className="w-3 h-3" />
                      Quantidade / Peso
                    </p>
                    <p className="text-lg font-bold text-purple-300">{quantidadeFormatada}</p>
                    {carga.tipo_carroceria && (
                      <p className="text-xs text-purple-400/70 mt-1">
                        Carroceria: {carga.tipo_carroceria}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <ChevronUp className="w-5 h-5 text-slate-500" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* RESUMO (quando não expandido) */}
          {!expandido && (
            <>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-500 uppercase mb-0.5">Origem</p>
                    <p className="font-semibold text-slate-200 truncate">
                      {carga.origem_cidade} <span className="text-slate-500 font-normal">({carga.origem_estado})</span>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-500 uppercase mb-0.5">Destino</p>
                    <p className="font-semibold text-slate-200 truncate">
                      {carga.destino_cidade} <span className="text-slate-500 font-normal">({carga.destino_estado})</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className={`${config.light} rounded-lg p-3 flex items-center gap-2 border border-slate-700`}>
                  <Truck className={`w-4 h-4 ${config.text}`} />
                  <span className="text-sm text-slate-300 font-medium truncate">{carga.tipo_veiculo}</span>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 flex items-center gap-2 border border-slate-700">
                  {iconeQuantidade}
                  <span className="text-sm text-slate-300 font-medium">{quantidadeFormatada}</span>
                </div>
              </div>

              {carga.tipo_carroceria && (
                <div className="bg-slate-800 rounded-lg p-3 flex items-center gap-2 border border-slate-700">
                  <Truck className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300 font-medium">{carga.tipo_carroceria}</span>
                </div>
              )}

              {modo === 'publico' && (
                <div className="flex items-center justify-center">
                  <motion.div 
                    animate={{ y: [0, 3, 0] }} 
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="flex items-center gap-1 text-xs text-slate-500"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Clique para mais detalhes
                  </motion.div>
                </div>
              )}
            </>
          )}

          {/* AÇÕES */}
          <div className="space-y-3 mt-auto pt-2">
            {renderizarAcoes()}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
});

CargaCard.displayName = 'CargaCard';

export default CargaCard;